import React from "react";
import { useForm } from "react-hook-form";
import {AccordionDetails, Box, Typography, useMediaQuery} from "@material-ui/core";
import ContentFilterValues from "../../../model/ContentFilter";
import initialContentFilterState from "../../../utils/initialStates/initialContentFilterState";
import { ContentFilterOptions } from "../../../utils/contentFilterOptions";
import ContentSearchField from "./ContentSearchField";
import FilterAccordion from "./filterAccordion/FilterAccordion";
import ContentSwitches from "./switches/ContentSwitches";
import ContentTutorialContainer from "./contentTutorial/ContentTutorialContainer";
import useContentFilterStyles from "./styles";
import { AllSwitchFilterNames, FilterExclusivity, SwitchFilterNames } from "./switches/helpers/switchTypes";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {useMfwThemeProvider} from "myfitworld-utils";
import contentMessages from "../messages";
import {useIntl} from "react-intl";
import { useShouldShowSwitches } from "src/hooks/contentFilters/switch/useShouldShowSwitches";
import { getSwitchesConfig } from "./switches/helpers/switchesConfig";

const ContentFilter = ({
  onFilterChange,
  itemCount,
  disabled = false,
  options,
  query,
  setQuery,
  onQueryChange,
  onAccordionToggle,
  entityName,
  contentFilterValues,
  loading
}: {
  onFilterChange: (filters: ContentFilterValues) => void;
  itemCount: number;
  disabled?: boolean;
  options: ContentFilterOptions;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  onQueryChange?: (value: string) => void;
  onAccordionToggle?: (value: boolean) => void;
  entityName?: string;
  contentFilterValues?: ContentFilterValues
  loading?:boolean
}) => {
  const { control, getValues, setValue, reset, register } = useForm<ContentFilterValues>({
    defaultValues: contentFilterValues ?? initialContentFilterState,
  });

  const classes = useContentFilterStyles();
  const theme = useMfwThemeProvider();
  const { mfwMuiWebTheme } = theme;
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(mfwMuiWebTheme.breakpoints.down('sm'));
  const visibilitySwitches = useShouldShowSwitches(options, entityName ?? "");
  const shouldRenderMobileSwitchesAccordion = getSwitchesConfig({
    visibilitySwitches,
    onExtraChange: () => undefined,
  }).some(({ condition }) => condition);

  const preSetValues = () => {
    const values = getValues();
    if (onFilterChange) onFilterChange(values);
  };

  function handleSwitchTrigger({e, switchName, switchType, onChange, onExtraChange}:{
    e: React.ChangeEvent<HTMLInputElement>,
    switchName: SwitchFilterNames,
    switchType: "multi" | "one_only",
    onChange: (v: any) => void,
    onExtraChange?: (checked: boolean) => void
  }) {

    const checked = e.target.checked;
    if (checked) {
      if (switchType === "one_only") {
        Object.values(AllSwitchFilterNames).forEach((key) => {
          if (key !== switchName) {
            setValue(key as SwitchFilterNames, false);
          }
        });
      } else {
        FilterExclusivity.one_only.forEach((key) => setValue(key, false));
      }
    }
    onChange(checked);
    preSetValues();

    if (onExtraChange) {
      onExtraChange(checked);
    }
  }

  const handleFilterChangeSwitch = (onChange: (v: any) => void) => (value: any) => {
    FilterExclusivity.one_only.forEach((e) => {
      setValue(e, false)
    });
    handleFilterChange(onChange)(value);
  };

  const handleFilterChange = (onChange: (v: any) => void) => (value: any) => {
    onChange(value);
    preSetValues();
  };

  return (
    <Box mt={1} mb={1}>
      <FilterAccordion
        control={control}
        disabled={disabled}
        options={options}
        onAccordionToggle={onAccordionToggle}
        getValues={getValues}
        register={register}
        reset={reset}
        handleSwitchTrigger={handleSwitchTrigger}
        handleFilterChangeSwitch={handleFilterChangeSwitch}
        preSetValues={preSetValues}
        onFilterChange={onFilterChange}
      />

      {isMobile && shouldRenderMobileSwitchesAccordion &&
        <Box className={classes.containerSwitches}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{formatMessage(contentMessages.librarySwitches)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContentSwitches
                entityName={entityName}
                preSetValues={preSetValues}
                disabled={disabled}
                control={control}
                options={options}
                setValue={setValue}
                handleSwitchTrigger={handleSwitchTrigger}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      }
      
      <Box className={classes.containerBottom}>
        <ContentSearchField
          query={query}
          setQuery={setQuery}
          onQueryChange={onQueryChange}
          itemCount={itemCount}
          loading={loading ?? false}
          entityName={entityName}
        />

        {!isMobile &&
          <Box className={classes.containerSwitches}>
            <ContentSwitches
              entityName={entityName}
              preSetValues={preSetValues}
              disabled={disabled}
              control={control}
              options={options}
              setValue={setValue}
              handleSwitchTrigger={handleSwitchTrigger}
            />
          </Box>
        }

        {!isMobile && <ContentTutorialContainer entityName={entityName} />}
      </Box>
    </Box>
  );
};

export default ContentFilter;
